import React, { useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, Copy, FileSpreadsheet, Loader2, Upload, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';

/**
 * A row as returned by POST /licenses/import/validate: the resolved license
 * fields, plus the original cell values under `_raw` and the backend's verdict.
 */
export interface ImportPreviewRow {
    _row: number;
    _raw?: Record<string, any>;
    _status: 'READY' | 'WARNING' | 'ERROR';
    _errors: string[];
    _warnings: string[];
    _isDuplicate?: boolean;
    _isValid: boolean;
    _licensePlanLabel?: string;
    [key: string]: any;
}

export interface ImportPreviewSummary {
    total: number;
    ready: number;
    warning: number;
    duplicate: number;
    invalid: number;
}

interface LicenseImportPreviewProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    rows: ImportPreviewRow[];
    summary?: ImportPreviewSummary;
    isImporting: boolean;
    onConfirm: () => void;
}

const text = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
};

/**
 * Every column of the import template, in template order. `value` prefers the
 * value the user actually typed in Excel so the dialog reads back like their
 * sheet, and falls back to the backend's resolved value for the fields where
 * the canonical form is the more useful thing to show (dates, booleans, plan).
 */
const FIELDS: { key: string; label: string; value: (row: ImportPreviewRow) => string; mono?: boolean }[] = [
    { key: 'softwareName', label: 'Software Name', value: (r) => text(r.softwareName ?? r._raw?.softwareName) },
    { key: 'vendor', label: 'Vendor', value: (r) => text(r.vendor ?? r._raw?.vendor) },
    { key: 'licensePlan', label: 'License Plan', value: (r) => text(r._licensePlanLabel ?? r._raw?.licensePlan) },
    { key: 'category', label: 'Category', value: (r) => text(r._raw?.category ?? r.category) },
    { key: 'type', label: 'Licensing Model', value: (r) => text(r._raw?.type ?? r.type) },
    { key: 'productKey', label: 'Product Key', value: (r) => text(r.productKey ?? r._raw?.productKey), mono: true },
    { key: 'contractId', label: 'Contract / Agreement ID', value: (r) => text(r.contractId ?? r._raw?.contractId), mono: true },
    { key: 'tenantId', label: 'Tenant / Account ID', value: (r) => text(r.tenantId ?? r._raw?.tenantId), mono: true },
    { key: 'cloudMode', label: 'Run in Cloud Mode', value: (r) => (typeof r.cloudMode === 'boolean' ? (r.cloudMode ? 'Yes' : 'No') : text(r._raw?.cloudMode)) },
    { key: 'totalSeats', label: 'Total Seats', value: (r) => text(r.totalSeats ?? r._raw?.totalSeats) },
    { key: 'unitPrice', label: 'Unit Price', value: (r) => text(r.unitPrice ?? r._raw?.unitPrice) },
    { key: 'currency', label: 'Currency', value: (r) => text(r.currency ?? r._raw?.currency) },
    { key: 'billingFrequency', label: 'Billing Frequency', value: (r) => text(r._raw?.billingFrequency ?? r.billingFrequency) },
    { key: 'commitmentTerm', label: 'Commitment Term', value: (r) => text(r._raw?.commitmentTerm ?? r.commitmentTerm) },
    { key: 'purchaseDate', label: 'Purchase Date', value: (r) => text(r.purchaseDate ?? r._raw?.purchaseDate) },
    { key: 'expiryDate', label: 'Expiry Date', value: (r) => text(r.expiryDate ?? r._raw?.expiryDate) },
    { key: 'nextRenewalDate', label: 'Next Renewal Date', value: (r) => text(r.nextRenewalDate ?? r._raw?.nextRenewalDate) },
    { key: 'noticePeriodDays', label: 'Notice Period', value: (r) => text(r._raw?.noticePeriodDays ?? r.noticePeriodDays) },
    { key: 'renewalStatus', label: 'Renewal Status', value: (r) => text(r._raw?.renewalStatus ?? r.renewalStatus) },
    { key: 'complianceRisk', label: 'Compliance Risk', value: (r) => text(r._raw?.complianceRisk ?? r.complianceRisk) },
    { key: 'notes', label: 'Notes', value: (r) => text(r.notes ?? r._raw?.notes) },
];

/**
 * Row tints, in two forms. A sticky column has the rest of the table scrolling
 * underneath it, so its background must be fully opaque — an alpha utility like
 * `bg-destructive/10` lets the columns below show straight through and the text
 * collides. The sticky variant therefore paints the tint as a background-image
 * layer over an opaque background-color, which the alpha utilities cannot do
 * (both would set background-color and the last one would win).
 */
const TONES = {
    ERROR: { cell: 'bg-destructive/10', sticky: 'sticky-cell-error' },
    WARNING: { cell: 'bg-warning/10', sticky: 'sticky-cell-warning' },
    READY: { cell: 'bg-background', sticky: 'sticky-cell' },
} as const;

/** Hairline that keeps a pinned column visually separated while content scrolls under it. */
const STICKY_LEFT_EDGE = 'shadow-[1px_0_0_0_hsl(var(--border))]';
const STICKY_RIGHT_EDGE = 'shadow-[-1px_0_0_0_hsl(var(--border))]';

/** The subset shown as columns in the collapsed desktop table. */
const SUMMARY_KEYS = ['softwareName', 'vendor', 'licensePlan', 'productKey', 'contractId', 'tenantId', 'totalSeats', 'purchaseDate', 'expiryDate'];
const SUMMARY_FIELDS = SUMMARY_KEYS.map((key) => FIELDS.find((f) => f.key === key)!);

const StatusBadge: React.FC<{ row: ImportPreviewRow }> = ({ row }) => {
    if (row._status === 'ERROR') {
        return (
            <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap', 'bg-destructive/10 text-destructive')}>
                {row._isDuplicate ? <Copy className="h-3 w-3" /> : <X className="h-3 w-3" />}
                {row._isDuplicate ? 'Duplicate' : 'Error'}
            </span>
        );
    }
    if (row._status === 'WARNING') {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning whitespace-nowrap">
                <AlertTriangle className="h-3 w-3" /> Warning
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success whitespace-nowrap">
            <Check className="h-3 w-3" /> Ready
        </span>
    );
};

const Messages: React.FC<{ row: ImportPreviewRow }> = ({ row }) => {
    if (row._errors.length === 0 && row._warnings.length === 0) return null;
    return (
        <ul className="space-y-1">
            {row._errors.map((message, i) => (
                <li key={`e${i}`} className="flex items-start gap-1.5 text-xs text-destructive">
                    <X className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{message}</span>
                </li>
            ))}
            {row._warnings.map((message, i) => (
                <li key={`w${i}`} className="flex items-start gap-1.5 text-xs text-warning">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{message}</span>
                </li>
            ))}
        </ul>
    );
};

const RowDetails: React.FC<{ row: ImportPreviewRow }> = ({ row }) => (
    <div className="space-y-3 bg-muted/30 p-3 sm:p-4">
        <Messages row={row} />
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            {FIELDS.map((field) => (
                <div key={field.key} className="flex flex-col border-b border-border/50 py-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
                    <dt className="text-xs text-muted-foreground">{field.label}</dt>
                    <dd className={cn('break-words text-sm sm:text-right', field.mono && 'font-mono text-xs')}>{field.value(row)}</dd>
                </div>
            ))}
        </dl>
    </div>
);

const LicenseImportPreview: React.FC<LicenseImportPreviewProps> = ({ open, onOpenChange, rows, summary, isImporting, onConfirm }) => {
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    const toggle = (rowNumber: number) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(rowNumber)) next.delete(rowNumber);
            else next.add(rowNumber);
            return next;
        });

    // The backend sends a summary; recomputing locally keeps the dialog correct
    // against an older backend that does not yet return one.
    const counts = useMemo<ImportPreviewSummary>(
        () =>
            summary ?? {
                total: rows.length,
                ready: rows.filter((r) => r._status === 'READY').length,
                warning: rows.filter((r) => r._status === 'WARNING').length,
                duplicate: rows.filter((r) => r._isDuplicate).length,
                invalid: rows.filter((r) => r._status === 'ERROR').length,
            },
        [rows, summary],
    );

    const importableCount = counts.ready + counts.warning;

    const chips: { label: string; count: number; className: string }[] = [
        { label: 'Total', count: counts.total, className: 'bg-muted text-foreground' },
        { label: 'Ready', count: counts.ready, className: 'bg-success/10 text-success' },
        { label: 'Warning', count: counts.warning, className: 'bg-warning/10 text-warning' },
        { label: 'Duplicate', count: counts.duplicate, className: 'bg-destructive/10 text-destructive' },
        { label: 'Invalid', count: counts.invalid, className: 'bg-destructive/10 text-destructive' },
    ];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex flex-col sm:max-w-6xl sm:max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-5 w-5 text-primary" />
                        Review License Import Data
                    </DialogTitle>
                </DialogHeader>

                <div className="flex flex-wrap gap-2">
                    {chips.map((chip) => (
                        <span key={chip.label} className={cn('rounded-full px-2.5 py-1 text-xs font-medium', chip.className)}>
                            {chip.label}: {chip.count}
                        </span>
                    ))}
                </div>

                {counts.invalid > 0 && (
                    <p className="text-xs text-muted-foreground">
                        Invalid and duplicate rows are excluded — the rest still import. Expand a row to see every imported value.
                    </p>
                )}

                {/* Mobile: card list. Each card expands to the full 21 fields. */}
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto sm:hidden">
                    {rows.map((row) => (
                        <div key={row._row} className={cn('overflow-hidden rounded-md border', row._status === 'ERROR' && 'border-destructive/40 bg-destructive/5')}>
                            <button
                                type="button"
                                onClick={() => toggle(row._row)}
                                aria-expanded={expanded.has(row._row)}
                                className="flex w-full items-center justify-between gap-2 p-3 text-left"
                            >
                                <div className="min-w-0">
                                    <div className="truncate text-sm font-medium">{FIELDS[0].value(row)}</div>
                                    <div className="truncate text-xs text-muted-foreground">
                                        Row {row._row} · {FIELDS[1].value(row)}
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    <StatusBadge row={row} />
                                    {expanded.has(row._row) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </div>
                            </button>
                            {expanded.has(row._row) && <RowDetails row={row} />}
                        </div>
                    ))}
                </div>

                {/* Desktop: horizontally scrollable table with sticky Row and Status columns. */}
                <div className="hidden min-h-0 flex-1 overflow-auto rounded-md border sm:block">
                    <table className="w-full min-w-max border-collapse text-sm">
                        <thead className="sticky top-0 z-20 bg-muted">
                            <tr>
                                <th className={cn('sticky left-0 z-30 border-b bg-muted p-2 text-left font-medium', STICKY_LEFT_EDGE)}>Row</th>
                                {SUMMARY_FIELDS.map((field) => (
                                    <th key={field.key} className="whitespace-nowrap border-b bg-muted p-2 text-left font-medium">
                                        {field.label}
                                    </th>
                                ))}
                                <th className={cn('sticky right-0 z-30 border-b bg-muted p-2 text-left font-medium', STICKY_RIGHT_EDGE)}>Validation</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const isOpen = expanded.has(row._row);
                                const tone = TONES[row._status] ?? TONES.READY;
                                return (
                                    <React.Fragment key={row._row}>
                                        <tr className={tone.cell}>
                                            <td className={cn('sticky left-0 z-10 border-b p-2', tone.sticky, STICKY_LEFT_EDGE)}>
                                                <button
                                                    type="button"
                                                    onClick={() => toggle(row._row)}
                                                    aria-expanded={isOpen}
                                                    aria-label={`${isOpen ? 'Hide' : 'Show'} all fields for row ${row._row}`}
                                                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                                                >
                                                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                    {row._row}
                                                </button>
                                            </td>
                                            {SUMMARY_FIELDS.map((field) => (
                                                <td key={field.key} className={cn('max-w-[240px] truncate border-b p-2', field.mono && 'font-mono text-xs')} title={field.value(row)}>
                                                    {field.value(row)}
                                                </td>
                                            ))}
                                            <td className={cn('sticky right-0 z-10 border-b p-2 align-top', tone.sticky, STICKY_RIGHT_EDGE)}>
                                                <div className="w-[320px] space-y-1">
                                                    <StatusBadge row={row} />
                                                    <Messages row={row} />
                                                </div>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr>
                                                <td colSpan={SUMMARY_FIELDS.length + 2} className="border-b p-0">
                                                    <RowDetails row={row} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col gap-3 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-muted-foreground">
                        {importableCount} of {counts.total} licenses can be imported.
                    </p>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={onConfirm} disabled={isImporting || importableCount === 0}>
                            {isImporting ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    <Upload className="mr-2 h-4 w-4" /> Confirm Import
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LicenseImportPreview;
